import { Flag } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { CreateReportRequest } from "../../features/messages/contracts/CreateReportRequest";
import { ReporterFace, reportItem } from "../../features/messages/slice-extensions/reports-slice-extension";
import ModalShell from "../modal/ModalShell";

/** At most this many characters in the optional line (Report.NoteMaxLength on the backend). */
export const REPORT_NOTE_MAX_LENGTH = 300;

const REASONS: CreateReportRequest["reason"][] = ["Harassment", "Inappropriate", "Spam", "Other"];

export interface ReportTarget {
  targetKind: CreateReportRequest["targetKind"];
  targetId: string;
  threadId?: string | null;
}

interface ReportDialogProps {
  face: ReporterFace;
  target: ReportTarget;
  onClose: () => void;
}

/**
 * The report of a message or a shared file (US-111, PD-0054): a reason from the short list and an
 * optional line. On success the reporter gets the thanks line and, for a message, the "Reported" row.
 */
const ReportDialog = ({ face, target, onClose }: ReportDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState<CreateReportRequest["reason"] | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setBusy(true);
    setError(false);
    const request: CreateReportRequest = { ...target, reason, note: note.trim() || null };
    const result = await dispatch(reportItem({ face, request }));
    setBusy(false);
    if (reportItem.rejected.match(result)) {
      setError(true);
      return;
    }
    toast.success(t("messages.report.thanks"));
    onClose();
  };

  const title = target.targetKind === "Message" ? t("messages.report.titleMessage") : t("messages.report.titleFile");

  return (
    <ModalShell onClose={onClose} labelledBy="report-dialog-title">
      <div className="p-6" data-testid="report-dialog">
        <h3 id="report-dialog-title" className="flex items-center gap-2 text-lg font-semibold text-grey-10 dark:text-grey-90">
          <Flag size={20} aria-hidden="true" />
          {title}
        </h3>
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm text-grey-20 dark:text-grey-80">{t("messages.report.reasonLabel")}</legend>
          <div className="flex flex-col gap-2">
            {REASONS.map((option) => (
              <label key={option} className="flex cursor-pointer items-center gap-2 text-sm text-grey-20 dark:text-grey-80">
                <input
                  type="radio"
                  name="report-reason"
                  checked={reason === option}
                  onChange={() => setReason(option)}
                  disabled={busy}
                />
                {t(`messages.report.reasons.${option}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("messages.report.noteLabel")}
          <textarea
            className="input mt-1 min-h-[80px]"
            value={note}
            maxLength={REPORT_NOTE_MAX_LENGTH}
            onChange={(event) => setNote(event.target.value)}
            disabled={busy}
          />
        </label>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{t("messages.report.failed")}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button type="button" className="btn btn-primary" disabled={!reason || busy} onClick={submit}>
            {t("messages.report.submit")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ReportDialog;
